import React, {useEffect, useMemo, useState} from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {Close as CloseIcon} from "@mui/icons-material";
import {
  useUserSearch,
  useCharactersByPlayerRealtime,
} from "../hooks/useFirestore";
import {FirestoreSession} from "../services/firebase/firestore";
import {SessionService} from "../services/api/sessionService";
import {useNotifications} from "./NotificationSystem";
import {checkCharacterLevelEligibility} from "../utils/levelUtils";

interface DMAddPlayerProps {
  isOpen: boolean;
  onClose: () => void;
  session: FirestoreSession;
}

const DMAddPlayer: React.FC<DMAddPlayerProps> = ({
  isOpen,
  onClose,
  session,
}) => {
  const {addNotification} = useNotifications();
  const [search, setSearch] = useState("");
  const {data: users, loading: usersLoading} = useUserSearch(search);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string>("");
  const {data: playerCharacters, loading: charsLoading} =
    useCharactersByPlayerRealtime(selectedPlayerId || null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSelectedPlayerId("");
      setSelectedCharacterId("");
      setSubmitting(false);
    }
  }, [isOpen]);

  const userResults = useMemo(() => {
    if (!users || users.length === 0) return [] as any[];
    // Return all users - DMs should be able to add any user to their sessions
    return users;
  }, [users]);

  const activeCharacters = useMemo(() => {
    if (!playerCharacters) return [] as any[];
    return playerCharacters.filter(
      (c: any) =>
        c.isActive && (c.status === "active" || typeof c.status === "undefined")
    );
  }, [playerCharacters]);

  const handleConfirm = async () => {
    if (!session?.id || !selectedPlayerId || !selectedCharacterId) return;
    setSubmitting(true);
    try {
      const res = await SessionService.dmAddPlayerToSession({
        sessionId: session.id!,
        playerId: selectedPlayerId,
        characterId: selectedCharacterId,
      });
      addNotification({
        type: "success",
        title: "Player Added",
        message: res?.message || "Player successfully added to session",
      });
      onClose();
    } catch (error: any) {
      addNotification({
        type: "error",
        title: "Add Failed",
        message: error?.message || "Failed to add player to session",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {borderRadius: 2, maxHeight: "90vh"},
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h5" component="h2">
          Add Player to {session?.name}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{pt: 1}}>
        <div className="env-form-group">
          <label htmlFor="dm-add-player-search">Search players</label>
          <input
            id="dm-add-player-search"
            className="env-input"
            placeholder="Type a player name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: {xs: "column", md: "row"},
          }}
        >
          <Box sx={{flex: 1}}>
            <div className="env-card-medium" style={{padding: 12}}>
              <div className="env-card-title">Players</div>
              <div style={{maxHeight: 280, overflowY: "auto"}}>
                {usersLoading ? (
                  <div className="env-loading-state">
                    <div className="loading-spinner"></div>
                    <p>Searching users...</p>
                  </div>
                ) : !search.trim() ? (
                  <div className="env-empty-state">
                    Start typing to search users
                  </div>
                ) : userResults.length === 0 ? (
                  <div className="env-empty-state">No users found</div>
                ) : (
                  userResults.map((u: any) => (
                    <div
                      key={u.uid}
                      className={`env-list-item ${selectedPlayerId === u.uid ? "env-list-item-selected" : ""}`}
                      onClick={() => {
                        setSelectedPlayerId(u.uid);
                        setSelectedCharacterId("");
                      }}
                      style={{cursor: "pointer", padding: 8}}
                    >
                      <div style={{fontWeight: 600}}>{u.displayName}</div>
                      <div style={{fontSize: 12, color: "#666"}}>{u.email}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Box>

          <Box sx={{flex: 1}}>
            <div className="env-card-medium" style={{padding: 12}}>
              <div className="env-card-title">Characters</div>
              <div style={{maxHeight: 280, overflowY: "auto"}}>
                {!selectedPlayerId ? (
                  <div className="env-empty-state">
                    Select a player to view characters
                  </div>
                ) : charsLoading ? (
                  <div className="env-loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading characters...</p>
                  </div>
                ) : activeCharacters.length === 0 ? (
                  <div className="env-empty-state">
                    No active characters for this player
                  </div>
                ) : (
                  activeCharacters.map((c: any) => {
                    let warningText: string | null = null;
                    try {
                      const levelCheck = checkCharacterLevelEligibility(
                        typeof c.level === "string"
                          ? parseInt(c.level, 10)
                          : c.level,
                        session.levelRange
                      );
                      if (!levelCheck.isEligible && levelCheck.warningMessage) {
                        warningText = levelCheck.warningMessage;
                      }
                    } catch (_) {
                      // ignore eligibility errors
                    }
                    return (
                      <div
                        key={c.id}
                        className={`env-character-item character-selection-item ${
                          selectedCharacterId === c.id
                            ? "character-selection-item-selected"
                            : ""
                        }`}
                        onClick={() => setSelectedCharacterId(c.id || "")}
                        style={{cursor: "pointer"}}
                      >
                        <div className="env-character-info">
                          <h3>{c.name}</h3>
                          <div className="env-character-class">
                            {c.species} {c.class} - Level{" "}
                            {typeof c.level === "string"
                              ? parseInt(c.level, 10)
                              : c.level}
                          </div>
                          {warningText && (
                            <div className={`level-warning level-warning-soft`}>
                              <i className="warning-icon">⚠️</i>
                              <span className="warning-text">
                                {warningText}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </Box>
        </Box>

        <Divider sx={{my: 2}} />

        <div className="env-confirm-dialog-actions">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!selectedPlayerId || !selectedCharacterId || submitting}
          >
            {submitting ? "Adding..." : "Add Player"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DMAddPlayer;
